import type { ReactNode } from "react";
import { useContext } from "react";
import {
  LuBookmark,
  LuCheck,
  LuCirclePlus as LuPlusCircle,
  LuImage,
  LuMinus,
  LuMusic,
} from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { Spinner } from "@/components/shared/spinner";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerSeparator,
  DropdownDrawerSub,
  DropdownDrawerSubContent,
  DropdownDrawerSubTrigger,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserContext } from "@/contexts/user-context";
import type { CollectionItem, CollectionMeta } from "@/hooks/use-collections";
import { useCollections } from "@/hooks/use-collections";

type AddToCollectionDropdownContentProps = {
  types: CollectionMeta["type"][];
  itemData: CollectionItem;
};

const getCollectionTypeLabel = (type: CollectionMeta["type"]) => {
  if (type === "bookmark") {
    return "Bookmarks";
  }

  if (type === "playlist") {
    return "Playlists";
  }

  return "Galleries";
};

function AddToCollectionDropdownContent({
  types,
  itemData,
}: AddToCollectionDropdownContentProps) {
  const {
    collections,
    addToCollection,
    collectionsLoading,
    removeFromCollection,
  } = useCollections();

  const collectionsByType = Object.entries(
    collections?.reduce(
      (acc, collection) => {
        if (!types.includes(collection.type)) {
          return acc;
        }
        if (!acc[collection.type]) {
          acc[collection.type] = [];
        }
        acc[collection.type].push(collection);
        return acc;
      },
      {} as { [key: string]: typeof collections },
    ) || {},
  );

  const getMenuItem = (collection: CollectionMeta) => {
    const itemIncluded = collection.items.find(
      (item) =>
        item.postHash === itemData.postHash &&
        item.type === itemData.type &&
        // @ts-expect-error If there is no clientId, it compares undefined with undefined, which is fine
        item.clientId === itemData.clientId,
    );
    return (
      <DropdownDrawerItem
        key={collection.id}
        onClick={() =>
          itemIncluded
            ? removeFromCollection(collection.id!, itemData)
            : addToCollection(collection.id!, itemData)
        }
        className="
          hover:[&_.check]:hidden
          hover:[&_.remove]:inline-flex
        "
      >
        <div className="flex w-full items-center gap-2">
          {collectionsByType.length === 1 && collection.type === "bookmark" && (
            <LuBookmark className="size-4 shrink-0" />
          )}
          {collectionsByType.length === 1 && collection.type === "playlist" && (
            <LuMusic className="size-4 shrink-0" />
          )}
          {collectionsByType.length === 1 && collection.type === "gallery" && (
            <LuImage className="size-4 shrink-0" />
          )}
          <span className="line-clamp-2">{collection.name}</span>
          {itemIncluded && (
            <span className="ml-auto shrink-0">
              <LuCheck className="check size-4" />
              <LuMinus className="remove ml-auto hidden size-4 text-destructive" />
            </span>
          )}
        </div>
      </DropdownDrawerItem>
    );
  };

  return (
    <>
      <div className="custom-scrollbar max-h-[300px] overflow-y-auto">
        {collectionsLoading && (
          <div className="flex items-center justify-center py-2">
            <Spinner size={24} />
          </div>
        )}
        {!collectionsLoading && collectionsByType.length === 0 && (
          <div
            className="
              flex items-center justify-center p-4 text-center text-sm
              text-balance text-muted
            "
          >
            You have no suitable collections yet.
          </div>
        )}
        {collectionsByType.length === 1 &&
          collectionsByType[0][1].map(getMenuItem)}
        {collectionsByType.length > 1 &&
          collectionsByType.map(([type, collections]) => (
            <DropdownDrawerSub key={type}>
              <DropdownDrawerSubTrigger
                key={type}
                className="hover:[&_svg]:text-primary"
              >
                <div className="flex items-center gap-2">
                  {type === "bookmark" && (
                    <>
                      <LuBookmark className="size-4" /> <span>Bookmarks</span>
                    </>
                  )}
                  {type === "playlist" && (
                    <>
                      <LuMusic className="size-4" /> <span>Playlists</span>
                    </>
                  )}
                  {type === "gallery" && (
                    <>
                      <LuImage className="size-4" /> <span>Galleries</span>
                    </>
                  )}
                </div>
              </DropdownDrawerSubTrigger>
              <DropdownDrawerSubContent
                title={getCollectionTypeLabel(type as CollectionMeta["type"])}
              >
                {collections.map(getMenuItem)}
              </DropdownDrawerSubContent>
            </DropdownDrawerSub>
          ))}
      </div>
    </>
  );
}

type AddToCollectionDropdownProps = AddToCollectionDropdownContentProps & {
  children: ReactNode;
  createText?: string;
  addToText?: string;
  align?: "start" | "center" | "end";
  onOpenChange?: (open: boolean) => void;
};

export function AddToCollectionDropdown({
  children,
  createText = "Create new collection",
  addToText = "Add to collection",
  align = "center",
  onOpenChange,
  ...props
}: AddToCollectionDropdownProps) {
  const { loggedIn } = useContext(UserContext);
  if (!loggedIn) {
    return null;
  }

  return (
    <>
      <DropdownDrawer
        historyName="add-to-collection"
        onOpenChange={onOpenChange}
        title={addToText}
      >
        <DropdownDrawerTrigger
          render={
            <div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>{children}</TooltipTrigger>
                  <TooltipContent>{addToText}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          }
        />
        <DropdownDrawerContent className="md:w-56" align={align}>
          <AddToCollectionDropdownContent {...props} />
          <DropdownDrawerSeparator />
          <DropdownDrawerItem
            className="gap-2"
            onClick={() =>
              setDialog(DIALOG.CreateCollection, {
                open: true,
                replace: true,
                params: {
                  defaultType: props.types[0],
                },
                props: {
                  defaultItems: props.itemData ? [props.itemData] : undefined,
                },
              })
            }
          >
            <LuPlusCircle className="size-4" />
            <span>{createText}</span>
          </DropdownDrawerItem>
        </DropdownDrawerContent>
      </DropdownDrawer>
    </>
  );
}
